import { useCallback, useEffect, useRef, useState } from 'react';
import { BuildingData } from '../../interfaces/building-interfaces';
import { createItem, fetchAllItems } from '../requests/requests';
import { User } from '../../interfaces/user-interfaces';
import axios from 'axios';
import { apiRoute } from '../../constants/api-constants';
import { ROLES, normalizeUserRole, isStaff } from '../../auth/roles';
import { publishBuildingZones } from '../../context/clock-context';
import {
  processDailyData,
  processMonthlyData,
  processVisitorData,
  processWeeklyData,
} from './format-analytics-data';

export enum TimePeriod {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
}

export const extractBuildingNames = (data: BuildingData[]): string[] => {
  return data.map(building => building.property_name);
};

export const extractBuildingIds = (data: BuildingData[]): string[] => {
  return data.map(building => String(building.id));
};

export const findBuildingById = (
  buildings: BuildingData[],
  buildingId?: string | number | null
): BuildingData | null => {
  if (buildingId === null || buildingId === undefined || buildingId === '') {
    return null;
  }

  return (
    buildings.find(building => String(building.id) === String(buildingId)) ||
    null
  );
};

/**
 * WHERE THE DESK'S CHOICE OF BUILDING IS REMEMBERED.
 *
 * It was remembered nowhere: the selection lived only in this hook's useState,
 * and `resolveSelectedBuilding` auto-picks only when there is exactly ONE
 * building. So a manager covering two or more re-picked their building on every
 * page load, every refresh and every return from the dashboard — several times
 * a shift, for the entire life of the account.
 *
 * Keyed BY USER, because a shared front-desk device is the normal case in this
 * product and the previous occupant's building is not a sensible default for the
 * next one. localStorage rather than session storage: surviving the tab being
 * closed is the entire point.
 */
const SELECTED_BUILDING_KEY = 'lobbero.selectedBuilding';

const rememberedBuildingId = (userId?: string | number | null): string | null => {
  if (userId == null) return null;
  try {
    return window.localStorage.getItem(`${SELECTED_BUILDING_KEY}.${userId}`);
  } catch {
    // Safari private mode and locked-down enterprise profiles both throw here.
    // Forgetting the choice is a mild annoyance; crashing the desk is not.
    return null;
  }
};

const rememberBuildingId = (
  userId: string | number | null | undefined,
  buildingId: string | null
): void => {
  if (userId == null) return;
  try {
    const key = `${SELECTED_BUILDING_KEY}.${userId}`;
    if (buildingId) window.localStorage.setItem(key, buildingId);
    else window.localStorage.removeItem(key);
  } catch {
    // As above.
  }
};

export const resolveSelectedBuilding = (
  buildings: BuildingData[],
  currentBuildingId?: string | null
): BuildingData | null => {
  if (buildings.length === 1) {
    return buildings[0];
  }

  if (!currentBuildingId) {
    return null;
  }

  // A remembered id that is no longer in the list — the building was archived,
  // or this user's access to it was revoked — resolves to null and the picker
  // opens, which is the correct outcome and not an error.
  return findBuildingById(buildings, currentBuildingId);
};

interface UseBuildingDataOptions {
  enabled?: boolean;
  loadAnalytics?: boolean;
}

const useBuildingData = (
  user: User | null | undefined,
  { enabled = true, loadAnalytics = true }: UseBuildingDataOptions = {}
) => {
  /* eslint-disable */
  const [dailyData, setDailyData] = useState<any>([]);
  const [weeklyData, setWeeklyData] = useState<any>([]);
  const [monthlyData, setMonthlyData] = useState<any>([]);
  /* eslint-enable */
  const [buildingData, setBuildingData] = useState<BuildingData[]>([]);
  /**
   * WHY A FAILED BUILDING FETCH NEEDS A NAME.
   *
   * The catch below used to console.error and then set the building list to [],
   * the names to [], the ids to [] and the selection to null — which is exactly
   * the state of a staff member who has not been given a building yet. So a
   * dropped connection, a 500 or an expired session rendered as "you work
   * nowhere": an empty building picker on every desk screen, the sidebar blank,
   * and app-router.tsx bouncing any building-bound route back to /main-menu
   * with no message. Nothing on screen said a request had failed.
   *
   * Empty and broken are different facts and the shell has to be able to tell
   * them apart, so the failure is kept rather than swallowed.
   */
  const [buildingsError, setBuildingsError] = useState<string | null>(null);
  const [buildingNames, setBuildingNames] = useState<string[]>([]);
  const [buildingIds, setBuildingIds] = useState<string[]>([]);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(TimePeriod.Daily);
  const [selectedBuilding, setSelectedBuilding] = useState<BuildingData | null>(
    null
  );
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(
    null
  );
  const [loadedUserKey, setLoadedUserKey] = useState<string | null>(null);
  const propertyRequestRef = useRef(0);
  const analyticsRequestRef = useRef(0);
  // Cache key only — 'none' rather than 'unknown' for a null role, because
  // since migration 037 that is a definite answer (no staff capability), not a
  // failure to read one.
  const userKey = `${user?.id || 'anonymous'}:${
    normalizeUserRole(user?.role) || 'none'
  }`;

  const fetchPropertiesForAdmin = async (): Promise<BuildingData[]> => {
    const response = await fetchAllItems('dashboard/buildings');

    return response as BuildingData[];
  };

  const fetchPropertiesForManager = async (
    user: User
  ): Promise<BuildingData[]> => {
    const response = await createItem('dashboard/buildings', user);

    return response as BuildingData[];
  };

  const getPropertiesfromDB = async (
    requestId: number,
    requestUserKey: string
  ) => {
    try {
      setBuildingsError(null);
      let data: BuildingData[] = [];

      // Residents can't list buildings (staff-only endpoint → 403). Skip the
      // fetch for them entirely instead of firing a doomed request (e.g. while
      // a resident briefly passes through /onboarding, which lives in AppRouter).
      // normalizeUserRole: users.role holds a staff capability or nothing
      // (migration 037). Gating on isStaff rather than on `!== RESIDENT` — the
      // latter was TRUE for a null-role resident and fired the doomed request
      // it existed to prevent.
      // A co-host (Independent Stays IS5) is staff without building:read: their
      // places are the listings shared with them (/stays/buildings), never the
      // buildings list, so the same doomed 403 is skipped for them too.
      const role = normalizeUserRole(user?.role);

      if (user && isStaff(user.role) && role !== ROLES.STAYS_COHOST) {
        if (role === ROLES.OWNER) {
          data = await fetchPropertiesForAdmin();
        } else {
          data = await fetchPropertiesForManager(user);
        }
      }

      if (requestId !== propertyRequestRef.current) {
        return;
      }

      if (Array.isArray(data) && data.every(item => typeof item === 'object')) {
        setBuildingData(data);
        // Every screen that needs a building's own zone looks it up by id
        // (useZoneOfBuilding) instead of being handed the list.
        publishBuildingZones(data);
        const names = extractBuildingNames(data);
        const ids = extractBuildingIds(data);
        setBuildingNames(names);
        setBuildingIds(ids);
        setSelectedBuilding(current =>
          // The remembered choice is the fallback, NOT an override: a selection
          // already made in this session wins, so a refetch cannot yank the
          // desk back to yesterday's building mid-shift.
          resolveSelectedBuilding(
            data,
            current?.id ?? rememberedBuildingId(user?.id)
          )
        );
      } else {
        console.error('Received data is not in the expected format.');
        setBuildingData([]);
        setBuildingNames([]);
        setBuildingIds([]);
        setSelectedBuilding(null);
      }
    } catch (error) {
      if (requestId === propertyRequestRef.current) {
        console.error('Failed to fetch buildings data:', error);
        // NOT cleared to []. Blanking the list is what made a failure
        // indistinguishable from "assigned to no building"; if a previous
        // answer is on screen, leaving it there and saying the refresh failed
        // is both truer and less destructive than emptying the picker.
        setBuildingsError(
          'We could not load your buildings. Check your connection and try again.'
        );
      }
    } finally {
      if (requestId === propertyRequestRef.current) {
        setLoadedUserKey(requestUserKey);
      }
    }
  };

  const selectBuildingById = useCallback(
    (buildingId: string) => {
      const next = findBuildingById(buildingData, buildingId);

      setSelectedBuilding(next);
      // Written on the EXPLICIT choice only. Persisting whatever the resolver
      // happened to land on would quietly promote a fallback into a preference.
      rememberBuildingId(user?.id, next ? String(next.id) : null);
    },
    [buildingData, user?.id]
  );

  const changeHandler = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>
      | string,
    fieldName?: string,
    explicitValue?: string
  ) => {
    let value: string;

    if (explicitValue !== undefined) {
      value = explicitValue;
    } else if (typeof e === 'string') {
      value = e;
    } else if ('value' in e.target) {
      value = e.target.value;
    } else {
      throw new Error('Unable to determine the value to set.');
    }

    if (fieldName === 'selectedBuilding') {
      const matchingBuilding = buildingData.find(
        b => b.property_name === value
      );
      setSelectedBuilding(matchingBuilding || null);
    }
  };

  // Stable user fields avoid refetching on harmless object-identity changes.
  // The request sequence also prevents stale responses replacing newer data.
  useEffect(() => {
    if (!enabled) {
      propertyRequestRef.current += 1;

      return;
    }

    const requestId = propertyRequestRef.current + 1;
    propertyRequestRef.current = requestId;
    getPropertiesfromDB(requestId, userKey);

    return () => {
      if (propertyRequestRef.current === requestId) {
        propertyRequestRef.current += 1;
      }
    };
  }, [enabled, user?.id, user?.role, userKey]);

  const fetchAnalyticsForSelectedBuilding = async (
    period: string,
    requestId: number
  ) => {
    if (selectedBuilding?.id && parseInt(selectedBuilding?.id)) {
      try {
        const response = await axios.get(
          `${apiRoute}/analytics/${period}/${selectedBuilding?.id}`
        );
        if (requestId !== analyticsRequestRef.current) {
          return;
        }
        const initialProcess = processVisitorData(response.data);

        switch (period) {
          case 'daily':
            const daily = processDailyData(initialProcess);
            setDailyData(daily);
            break;
          case 'weekly':
            const weekly = processWeeklyData(initialProcess);
            setWeeklyData(weekly);
            break;
          case 'monthly':
            const monthly = processMonthlyData(initialProcess);
            setMonthlyData(monthly);
            break;
          default:
            console.error('Invalid analytics period:', period);
            break;
        }
      } catch (error) {
        if (requestId === analyticsRequestRef.current) {
          console.error(`Error fetching ${period} data:`, error);
        }
      }
    }
  };

  useEffect(() => {
    if (!enabled || !loadAnalytics) {
      analyticsRequestRef.current += 1;

      return;
    }

    const requestId = analyticsRequestRef.current + 1;
    analyticsRequestRef.current = requestId;

    switch (timePeriod) {
      case TimePeriod.Daily:
        fetchAnalyticsForSelectedBuilding('daily', requestId);

        break;
      case TimePeriod.Weekly:
        fetchAnalyticsForSelectedBuilding('weekly', requestId);

        break;
      case TimePeriod.Monthly:
        fetchAnalyticsForSelectedBuilding('monthly', requestId);

        break;
      default:
        console.error('Invalid time period:', timePeriod);
        break;
    }

    return () => {
      if (analyticsRequestRef.current === requestId) {
        analyticsRequestRef.current += 1;
      }
    };
  }, [enabled, loadAnalytics, selectedBuilding?.id, timePeriod]);

  useEffect(() => {
    setSelectedBuildingId(selectedBuilding ? selectedBuilding.id : null);
  }, [selectedBuilding]);

  return {
    changeHandler,
    selectedBuilding,
    buildingData,
    buildingNames,
    buildingIds,
    // Existing same-user data stays visible while it is revalidated. Only an
    // unresolved user context is a blocking load, which avoids a sidebar flash
    // every time someone returns from the dashboard.
    loading: enabled && loadedUserKey !== userKey,
    /** Null unless the last load FAILED. Distinct from an empty list, which is
     *  a legitimate answer meaning "assigned to no building yet". */
    buildingsError,
    selectBuildingById,
    timePeriod,
    setTimePeriod,
    dailyData,
    monthlyData,
    weeklyData,
    selectedBuildingId,
  };
};

export default useBuildingData;
